import { motion } from "framer-motion";
import { TrendingUp, Eye, MousePointerClick, Timer } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const metrics = [
  {
    icon: Eye,
    value: 300,
    suffix: "%",
    label: "Plus de vues",
    description: "Temps passé sur votre site vs une simple page de présentation",
    color: "from-primary to-accent",
  },
  {
    icon: MousePointerClick,
    value: 3,
    suffix: "x",
    label: "Plus de contacts",
    description: "Taux de prise de contact avec un portfolio professionnel",
    color: "from-accent to-primary",
  },
  {
    icon: Timer,
    value: 2,
    suffix: "min+",
    label: "Temps de visite",
    description: "vs 6 secondes sur une page classique",
    color: "from-primary to-accent",
  },
  {
    icon: TrendingUp,
    value: 100,
    suffix: "%",
    label: "Satisfaction client",
    description: "Nos clients recommandent notre service",
    color: "from-accent to-primary",
  },
];

function Counter({ value, suffix }: { value: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const done = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !done.current) {
        done.current = true;
        const t0 = Date.now();
        const run = () => {
          const p = Math.min((Date.now() - t0) / 1200, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setCount(Math.round(eased * value));
          if (p < 1) requestAnimationFrame(run);
        };
        run();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const SellResults = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/3 to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            📊 Résultats prouvés
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            L'impact est{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              mesurable
            </span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Les chiffres parlent d'eux-mêmes
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {metrics.map((metric, i) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative p-7 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 hover:border-primary/40 transition-all duration-300 text-center">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${metric.color} flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/15`}>
                  <metric.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <p className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-1">
                  <Counter value={metric.value} suffix={metric.suffix} />
                </p>
                <p className="font-bold text-foreground mb-1">{metric.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{metric.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SellResults;
